/**
 * TABLE TYPE DEFINITIONS
 */

import { ColumnDefinition } from './column.types';

export interface TableData {
  [rowKey: string]: {
    [columnKey: string]: any;
  };
}

export interface CellPosition {
  rowIndex: number;
  colIndex: number;
}

export interface TableStyleConfig {
  condensed?: boolean;
  headerHeight?: number;
  unitRowHeight?: number;
  rowHeight?: number;
  baseFontSize?: number;
  headerFontSize?: number;
  unitFontSize?: number;
  fontFamily?: string;
  padding?: string;
  borderColor?: string;
  headerBgColor?: string;
  rowBgColor?: string;
  alternateRowBgColor?: string;
  stripedRows?: boolean;
  hoverEffect?: boolean;
  showBorders?: boolean;
  virtualScrolling?: boolean;
  lazyLoading?: boolean;
}

export const DEFAULT_STYLE_CONFIG: TableStyleConfig = {
  condensed: true,
  headerHeight: 32,
  unitRowHeight: 22,
  rowHeight: 28,
  baseFontSize: 13, // Changed from 11 to 13 for consistency with Ambient and BD Details
  headerFontSize: 13, // Changed from 10 to 13 for consistency
  unitFontSize: 11,
  fontFamily: 'var(--font-primary)',
  padding: '2px 4px',
  borderColor: 'var(--color-gray-300)',
  headerBgColor: 'var(--color-gray-200)',
  rowBgColor: 'var(--color-white)',
  alternateRowBgColor: 'var(--color-gray-50)',
  stripedRows: false,
  hoverEffect: true,
  showBorders: true,
  virtualScrolling: false,
  lazyLoading: false
};

export const EXPANDED_STYLE_CONFIG: TableStyleConfig = {
  ...DEFAULT_STYLE_CONFIG,
  condensed: false,
  headerHeight: 44,
  unitRowHeight: 26,
  rowHeight: 38,
  baseFontSize: 14,
  headerFontSize: 13,
  unitFontSize: 12,
  padding: '8px 12px'
};

/**
 * Main table props
 */
export interface UniversalTableProps {
  // Data
  columns: ColumnDefinition[];
  data: TableData;
  rowKeys: string[];

  // Configuration
  styleConfig?: TableStyleConfig;

  // State
  focusedCell: CellPosition;
  loading?: boolean;
  error?: string;
  topRowContent?: React.ReactNode;

  // Callbacks
  onCellChange: (rowKey: string, columnKey: string, value: any) => void;
  onRowKeyChange?: (index: number, newKey: string) => void;
  onRemoveRowKey?: (index: number) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, row: number, col: number) => void;
  onFocus: (row: number, col: number) => void;
  onRowClick?: (rowKey: string, rowIndex: number) => void;
  onCellClick?: (rowKey: string, columnKey: string, rowIndex: number, colIndex: number) => void;

  // Refs
  setInputRef: (el: HTMLInputElement | null, row: number, col: number) => void;
  containerRef: React.RefObject<HTMLDivElement | null>; // ✅ FIXED: Added | null

  // Features
  enableRowSelection?: boolean;
  enableMultiSelect?: boolean;
  enableContextMenu?: boolean;
  enableCopyPaste?: boolean;

  // Customization
  customCellRenderer?: (
    column: ColumnDefinition,
    value: any,
    rowKey: string,
    rowIndex: number,
    colIndex: number
  ) => React.ReactNode;

  customRowRenderer?: (
    rowKey: string,
    rowIndex: number,
    rowData: any
  ) => React.ReactNode;
}

export interface TableContext {
  columns: ColumnDefinition[];
  styleConfig: TableStyleConfig;
  focusedCell: CellPosition;
  setFocusedCell: (position: CellPosition) => void;
}
